import { App } from '@capacitor/app';

const INACTIVITY_TIMEOUT_MS = 60_000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'] as const;

export const useInactivityTimer = () => {
  let timerId: ReturnType<typeof setTimeout> | null = null;
  let lockFn: (() => void) | null = null;
  let appStateListener: { remove: () => void } | null = null;
  let backgroundedAt: number | null = null;

  const resetTimer = () => {
    if (!lockFn) {
      return;
    }
    if (timerId !== null) {
      clearTimeout(timerId);
    }
    timerId = setTimeout(lockFn, INACTIVITY_TIMEOUT_MS);
  };

  const handleActivity = () => resetTimer();

  const start = async (onLock: () => void) => {
    lockFn = onLock;

    ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    appStateListener = await App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        backgroundedAt = Date.now();
        if (timerId !== null) {
          clearTimeout(timerId);
        }
      } else {
        const elapsed = backgroundedAt !== null ? Date.now() - backgroundedAt : 0;
        backgroundedAt = null;
        if (elapsed >= INACTIVITY_TIMEOUT_MS) {
          lockFn?.();
        } else {
          resetTimer();
        }
      }
    });

    resetTimer();
  };

  const stop = () => {
    if (timerId !== null) {
      clearTimeout(timerId);
    }
    timerId = null;
    lockFn = null;
    backgroundedAt = null;

    ACTIVITY_EVENTS.forEach(event => {
      document.removeEventListener(event, handleActivity);
    });

    appStateListener?.remove();
    appStateListener = null;
  };

  return { start, stop };
};
